import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Chip,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Avatar
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { usePermissions } from '../../utils/permissions';
import { supabase } from '../../supabaseClient';
import InsightsIcon from '@mui/icons-material/Insights';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MetricStatCard from './accounting-overview-components/MetricStatCard';
import CashFlowMiniChart from './accounting-overview-components/CashFlowMiniChart';
import { HeaderCard, SectionPaper } from './accounting-overview-components/layout';

const AccountingOverviewPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { canViewAccountingOverview } = usePermissions();
  const [companyName, setCompanyName] = useState('');

  const fetchCompanyData = async () => {
    if (!companyId) return;
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  const summaryMetrics = [
    {
      title: 'Cash On Hand',
      value: '$2,468,920',
      helperText: 'Liquid + operating accounts',
      trend: { direction: 'up', label: '+$148k vs last month' },
      icon: <PaidIcon sx={{ fontSize: 28 }} />,
      accent: 'success'
    },
    {
      title: 'Monthly Revenue',
      value: '$812,450',
      helperText: 'Closed & scheduled invoices',
      trend: { direction: 'up', label: '+12% growth' },
      icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
      accent: 'primary'
    },
    {
      title: 'Net Profit',
      value: '$182,140',
      helperText: '22.4% operating margin',
      trend: { direction: 'up', label: '+$36k vs target' },
      icon: <AssessmentIcon sx={{ fontSize: 28 }} />,
      accent: 'info'
    },
    {
      title: 'Cash Burn Coverage',
      value: '9.8 months',
      helperText: 'Runway at current spend',
      trend: { direction: 'down', label: '-0.4 months (hiring)' },
      icon: <TimelineIcon sx={{ fontSize: 28 }} />,
      accent: 'warning'
    }
  ];

  const cashFlowSeries = useMemo(
    () => [
      { label: 'Jun', value: 126000 },
      { label: 'Jul', value: 182000 },
      { label: 'Aug', value: -24000 },
      { label: 'Sep', value: 98000 },
      { label: 'Oct', value: 154000 },
      { label: 'Nov', value: 211000 }
    ],
    []
  );

  const treasurySignals = [
    { label: 'Collections pace', detail: '84% within 15 days', status: 'good' },
    { label: 'DSO trend', detail: '34 → 28 days', status: 'good' },
    { label: 'Unreconciled items', detail: '3 transactions', status: 'watch' },
    { label: 'FX exposure', detail: '$42k (EUR)', status: 'watch' }
  ];

  const alerts = [
    { title: 'Inventory build in Feb', detail: 'Working capital spike expected', severity: 'warning' },
    { title: 'Wholesale rollout', detail: 'Need ACH mandate for 2 customers', severity: 'info' },
    { title: 'Close prep', detail: 'All journals drafted except payroll', severity: 'success' }
  ];

  // Permission check - AFTER all hooks
  if (!canViewAccountingOverview()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <InsightsIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Accounting Overview Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Accounting Overview. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: 10 }}>
      <HeaderCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <InsightsIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 400, color: 'primary.main', mb: 0.5 }}>
                Finance Operations Suite
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem',
                mb: 1
              }}>
                {companyName}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
                Real-time view across banking, receivables, payables, and forecasts. All numbers below are easy-to-follow samples so the workflow feels complete.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}>
                Close Books
              </Button>
              <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}>
                Sync Banks
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </HeaderCard>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        {summaryMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.title}>
            <MetricStatCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 1, mt: 3 }}>
        <Grid item xs={12} md={7}>
          <SectionPaper>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 400 }}>
                  Rolling Cash Flow
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Net inflows across bank and payment channels (last 6 months)
                </Typography>
              </Box>
            </Stack>
            <CashFlowMiniChart data={cashFlowSeries} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} mt={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Avg. Monthly Inflow
                </Typography>
                <Typography variant="h6">$168,400</Typography>
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Approval to Cash
                </Typography>
                <Typography variant="h6">6.4 days</Typography>
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Variance vs Plan
                </Typography>
                <Typography variant="h6" color="success.main">
                  +$42,800
                </Typography>
              </Box>
            </Stack>
          </SectionPaper>
        </Grid>
        <Grid item xs={12} md={5}>
          <SectionPaper>
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
              Profitability Bridge
            </Typography>
            {[
              { label: 'Gross Margin', value: '54%', helper: '+2.1 pts YoY', color: theme.palette.success.main },
              { label: 'Inventory Carry Cost', value: '$42,600', helper: 'Fully absorbed', color: theme.palette.warning.main },
              { label: 'Fulfillment Efficiency', value: '92%', helper: '3-day SLA met', color: theme.palette.info.main },
              { label: 'Operating Expense', value: '$398,700', helper: 'Within budget', color: theme.palette.success.main }
            ].map((item) => (
              <Box key={item.label} sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: item.color }}>
                    {item.value}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {item.helper}
                </Typography>
              </Box>
            ))}
          </SectionPaper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} md={6}>
          <SectionPaper>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 400 }}>
                  Treasury Signals
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automated checks to keep controllers proactive
                </Typography>
              </Box>
              <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
                Refresh
              </Button>
            </Stack>
            <Stack spacing={2}>
              {treasurySignals.map((signal) => (
                <Stack
                  key={signal.label}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.4)}` }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor:
                        signal.status === 'good'
                          ? alpha(theme.palette.success.main, 0.12)
                          : alpha(theme.palette.warning.main, 0.12),
                      color: signal.status === 'good' ? theme.palette.success.main : theme.palette.warning.main
                    }}
                  >
                    {signal.status === 'good' ? <CheckCircleOutlineIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {signal.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {signal.detail}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </SectionPaper>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionPaper>
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
              Cash Conversion Cycle
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Inventory', days: 28 },
                { label: 'Receivables', days: 32 },
                { label: 'Payables', days: 24 }
              ].map((stage) => (
                <Grid item xs={12} md={4} key={stage.label}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                      textAlign: 'center',
                      py: 2
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 400 }}>
                      {stage.days} d
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stage.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionPaper>
        </Grid>
      </Grid>

      <SectionPaper>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              Close Checklist
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Snapshot of this month’s progress
            </Typography>
          </Box>
          <Chip label="Due in 4 days" color="primary" variant="outlined" />
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { task: 'AR subledger tied out', status: 'Complete', owner: 'M. Lopez', updated: 'Today' },
              { task: 'Inventory cost roll', status: 'In progress', owner: 'J. Patel', updated: '2 hrs ago' },
              { task: 'Payroll accrual', status: 'Pending', owner: 'Finance Ops', updated: '—' }
            ].map((row) => (
              <TableRow key={row.task} hover>
                <TableCell>{row.task}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={
                      row.status === 'Complete' ? 'success' : row.status === 'In progress' ? 'warning' : 'default'
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{row.owner}</TableCell>
                <TableCell align="right">{row.updated}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionPaper>

      <SectionPaper>
        <Typography variant="h6" sx={{ fontWeight: 400, mb: 2 }}>
          Highlights
        </Typography>
        <Grid container spacing={2}>
          {alerts.map((alert) => (
            <Grid item xs={12} md={4} key={alert.title}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                  p: 2,
                  height: '100%'
                }}
              >
                <Stack direction="row" spacing={1.5}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor:
                        alert.severity === 'warning'
                          ? alpha(theme.palette.warning.main, 0.15)
                          : alert.severity === 'success'
                            ? alpha(theme.palette.success.main, 0.15)
                            : alpha(theme.palette.info.main, 0.15),
                      color:
                        alert.severity === 'warning'
                          ? theme.palette.warning.main
                          : alert.severity === 'success'
                            ? theme.palette.success.main
                            : theme.palette.info.main
                    }}
                  >
                    {alert.severity === 'warning' ? <WarningAmberIcon fontSize="small" /> : <CreditScoreIcon fontSize="small" />}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {alert.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {alert.detail}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </SectionPaper>
    </Container>
  );
};

export default AccountingOverviewPage;

